import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  DecoratedPostFile,
  UndecoratedPostFile,
} from '@flemingmetrics/api-client';
import {
  filterByValue,
  mapToKey,
  mergeWithoutDuplicates,
} from '@/utils/arrays';
import { buildCompositeKey, Identifier } from '@/store/utils';
import { deleteFile, deleteFiles } from './filesSlice';
import { setCompanyPosts } from '@/store/slices/companyPostsSlice';

type PostFilesState = {
  byId: Record<string, UndecoratedPostFile>;
  byPostId: Record<string, string[]>;
  byFileId: Record<string, string>;
};

const initialState: PostFilesState = {
  byId: {},
  byPostId: {},
  byFileId: {},
};

export const postFilesSlice = createSlice({
  name: 'postFiles',
  initialState,
  reducers: {
    addPostFile: (state, action: PayloadAction<DecoratedPostFile>) => {
      return getNextState(state, [action.payload]);
    },
    setPostFiles: (state, action: PayloadAction<DecoratedPostFile[]>) => {
      return getNextState(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setCompanyPosts, (state, action) => {
        return getNextState(
          state,
          mapToKey(action.payload, 'post').flatMap((p) => p.postFiles)
        );
      })
      .addCase(deleteFile, (state, action) => {
        const { fileId } = action.payload;
        let byId = { ...state.byId };
        let byPostId = { ...state.byPostId };
        let byFileId = { ...state.byFileId };
        const key = byFileId[fileId];
        const postFile = byId[key];
        if (!postFile) return state;
        if (byPostId[postFile.postId]) {
          byPostId[postFile.postId] = filterByValue(
            byPostId[postFile.postId],
            key
          );
        }
        delete byFileId[fileId];
        delete byId[key];
        return { byId, byPostId, byFileId };
      })
      .addCase(deleteFiles, (state, action) => {
        let byId = { ...state.byId };
        let byPostId = { ...state.byPostId };
        let byFileId = { ...state.byFileId };
        action.payload.fileIds.forEach((fileId) => {
          const key = byFileId[fileId];
          const postFile = byId[key];
          if (!postFile) return state;
          if (byPostId[postFile.postId]) {
            byPostId[postFile.postId] = filterByValue(
              byPostId[postFile.postId],
              key
            );
          }
          delete byFileId[fileId];
          delete byId[key];
        });

        return { byId, byPostId, byFileId };
      });
  },
});

function formatPostFile({
  file,
  ...postFile
}: DecoratedPostFile): UndecoratedPostFile {
  return postFile;
}

function getNextState(state: PostFilesState, postFiles: DecoratedPostFile[]) {
  const byId = { ...state.byId };
  const byPostId = { ...state.byPostId };
  const byFileId = { ...state.byFileId };
  postFiles.forEach((pf) => {
    const { postId, fileId } = pf;
    const key = buildPostFileKey(postId, fileId);
    byId[key] = formatPostFile(pf);
    if (!byPostId[postId]) byPostId[postId] = [];
    byPostId[postId] = mergeWithoutDuplicates(byPostId[postId], [key]);
    byFileId[fileId] = key;
  });
  return { byId, byPostId, byFileId };
}

export function buildPostFileKey(postId: Identifier, fileId: Identifier) {
  return buildCompositeKey(postId, fileId);
}

export const { addPostFile, setPostFiles } = postFilesSlice.actions;

export default postFilesSlice.reducer;
